import type { Feed, FeedEntry, UIComponent } from '~/services/layout';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getLimit, isEmptyArray } from '~/services/layout/layout';
import logger from '~/services/logger';
import { requestJsonData } from '~/utils/request-json-data';
import { v4 as uuidv4 } from 'uuid';
import { useSearchParams } from '@remix-run/react';
import { getFormScreenRuntime } from '~/services/plugins/quick-brick-form-screen/form-screen-state.ts';
import {
  selectEntryParentBehavior,
  selectEntryParentPreferenceEditorOptions,
  getSelectedCell,
  getUnselectedCell,
  executeCellActions,
  parseMultiSelectValue,
  toggleMultiSelectValue,
} from './helpers';
import { useActiveProfileId } from './use-active-profile-id';
import type { Action } from '../actions/types';
import { uiComponentRequiresRemoteClientContext } from '~/services/plugins/zapp-context-setter/utils/index.ts';

const PREFERENCE_EDITOR_ROLE: string = 'preference_editor';
const PREFERENCE_EDITOR_CONSUMER_TAG: string = 'preference_editor_consumer';
const PREFERENCE_EDITOR_TIMEOUT: number = 12_000;
const channelName: string = PREFERENCE_EDITOR_ROLE + uuidv4();

type PreferenceEditorOptions = {
  isInitialLoad: boolean;
  feedEntryExtensionsTag: string;
  role: string | undefined;
  byClick: boolean;
  screenScopedKey?: string;
};

export function usePreferenceEditor(
  feedEntry: FeedEntry,
  uiComponent: UIComponent
): {
  isPreferenceEditor: boolean;
  isBehaviorMultiActionFeedEntry: boolean;
  isPreferenceEditorCellAndUnselected: boolean;
  isPreferenceEditorCellAndSelected: boolean;
  preferenceEditorActionFunction: Function;
} {
  const role: string | undefined = feedEntry?._role;
  const {
    selectMode: behaviorSelectMode,
    currentSelection: behaviorCurrentSelection,
  } = selectEntryParentBehavior(feedEntry);

  const isPreferenceEditor: boolean = role === PREFERENCE_EDITOR_ROLE;

  const hasTapActions: boolean = !isEmptyArray(
    feedEntry?.extensions?.tap_actions?.actions as Action[]
  );

  const isBehaviorMultiActionFeedEntry: boolean =
    (behaviorSelectMode !== undefined || hasTapActions) && !isPreferenceEditor;

  const feedEntryExtensionsTag: string | undefined =
    feedEntry?.extensions?.tag || feedEntry?.id;
  const activeProfileId: string | null = useActiveProfileId();

  const preference_editor_options =
    selectEntryParentPreferenceEditorOptions(feedEntry);

  const initialValue = preference_editor_options?.initial_value;

  const screenScopedKey =
    preference_editor_options?.scope === 'screen' &&
    typeof preference_editor_options?.key === 'string'
      ? preference_editor_options.key
      : undefined;

  const screenStoredValue =
    screenScopedKey !== undefined
      ? getFormScreenRuntime()?.state.getValue(screenScopedKey)
      : undefined;

  const resolvedInitialValue = screenStoredValue ?? initialValue ?? '';
  const isMultiSelect: boolean =
    preference_editor_options?.select_mode === 'multi';

  const isInitialValueSelected: boolean =
    typeof resolvedInitialValue === 'string' &&
    typeof feedEntryExtensionsTag === 'string' &&
    (isMultiSelect
      ? parseMultiSelectValue(resolvedInitialValue).includes(
          feedEntryExtensionsTag
        )
      : resolvedInitialValue === feedEntryExtensionsTag);

  const [selected, setSelected] = useState<string>(resolvedInitialValue);

  // Only create BroadcastChannel on the client (not during SSR)
  // This prevents MessagePort leaks on the server
  // Use useMemo to ensure it's only created once per component instance
  const selectedChannel = useMemo(() => {
    return typeof window !== 'undefined'
      ? new BroadcastChannel(channelName)
      : null;
  }, []);

  const isLoadedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isPreferenceEditor || isLoadedRef.current || !selectedChannel) return;

    isLoadedRef.current = true;

    const onMessage = (event: MessageEvent) => {
      const eventData: PreferenceEditorOptions | undefined = event.data;
      if (eventData?.screenScopedKey !== screenScopedKey) return;
      if (typeof eventData?.feedEntryExtensionsTag !== 'string') return;

      setSelected(eventData.feedEntryExtensionsTag);
    };

    if (isInitialValueSelected) {
      selectedChannel.postMessage({
        isInitialLoad: !!resolvedInitialValue,
        feedEntryExtensionsTag: resolvedInitialValue,
        role,
        byClick: false,
        screenScopedKey,
      });
    }

    selectedChannel.addEventListener('message', onMessage);
    return () => {
      if (selectedChannel) {
        selectedChannel.removeEventListener('message', onMessage);
        try {
          selectedChannel.close();
        } catch {}
      }
    };
  }, []);

  const preferenceEditorActionFunction: Function = async () => {
    if (isBehaviorMultiActionFeedEntry) {
      if (
        !isEmptyArray(feedEntry?.extensions?.tap_actions?.actions as Action[])
      ) {
        executeCellActions(
          { actions: feedEntry?.extensions?.tap_actions?.actions as Action[] },
          {
            feedEntry,
            uiComponent,
          }
        );
      }
      return;
    }

    if (!isPreferenceEditor || !feedEntryExtensionsTag || !selectedChannel)
      return;

    const nextSelected: string = isMultiSelect
      ? toggleMultiSelectValue(selected, feedEntryExtensionsTag)
      : feedEntryExtensionsTag;

    if (!isMultiSelect && feedEntryExtensionsTag === selected) return;

    setSelected(nextSelected);

    if (screenScopedKey) {
      getFormScreenRuntime()?.state.setValue(screenScopedKey, nextSelected);
    }

    selectedChannel.postMessage({
      isInitialLoad: false,
      feedEntryExtensionsTag: nextSelected,
      role,
      byClick: true,
      screenScopedKey,
    });
  };

  const cellArgs = {
    isPreferenceEditor,
    isBehaviorMultiActionFeedEntry,
    behaviorCurrentSelection,
    selected,
    feedEntryExtensionsTag,
    feedEntryId: feedEntry?.id,
    isMultiSelect,
    activeProfileId,
  };

  return {
    isPreferenceEditor,
    isBehaviorMultiActionFeedEntry,
    isPreferenceEditorCellAndSelected: getSelectedCell(cellArgs),
    isPreferenceEditorCellAndUnselected: getUnselectedCell(cellArgs),
    preferenceEditorActionFunction,
  };
}

export function usePreferenceEditorPayload({
  feed,
  uiComponent,
  setFeedEntries,
}: {
  feed: Feed | undefined;
  uiComponent: UIComponent;
  setFeedEntries: Function;
}): void {
  // Only create BroadcastChannel on the client (not during SSR)
  // This prevents MessagePort leaks on the server
  // Use useMemo to ensure it's only created once per component instance
  const payloadChannel = useMemo(() => {
    return typeof window !== 'undefined'
      ? new BroadcastChannel(channelName)
      : null;
  }, []);

  // Storing entries in a ref for accessing from the timeout
  const serverLoadedFeedEntryRef = useRef<FeedEntry[]>(
    feed?.entry?.slice(0, getLimit(uiComponent?.rules?.item_limit)) || []
  );

  // Instantly track feed changes to update the ref value
  serverLoadedFeedEntryRef.current =
    feed?.entry?.slice(0, getLimit(uiComponent?.rules?.item_limit)) || [];

  const role = feed?.extensions?.role;
  const isPreferenceEditor: boolean = role === PREFERENCE_EDITOR_ROLE;

  // Access current URL and check for screen-feed query param
  const [searchParams] = useSearchParams();
  const screenFeed = searchParams.get('screen-feed');

  const isPreferenceEditorByObserveStorageEndpointTag: boolean =
    getIsPreferenceEditorByObserveStorageEndpointTag({
      uiComponent,
      feed,
    });

  useEffect(() => {
    if (
      isPreferenceEditor ||
      !isPreferenceEditorByObserveStorageEndpointTag ||
      !payloadChannel
    )
      return;

    let isServerLoadedFeedEntry: boolean = true;

    const timeoutId = setTimeout(() => {
      if (!isServerLoadedFeedEntry) return;
      setFeedEntries(serverLoadedFeedEntryRef.current);
    });

    const onMessage = async (event: MessageEvent) => {
      try {
        isServerLoadedFeedEntry = false;

        const eventData: PreferenceEditorOptions = event?.data;

        if (!eventData) throw new Error(`No event data found`);

        const role = eventData?.role;

        if (role !== PREFERENCE_EDITOR_ROLE)
          throw new Error(`Role is not preference_editor`);

        const isInitialLoad: boolean = eventData?.isInitialLoad;

        if (!isInitialLoad && !eventData?.byClick) return;

        const feedPath: string | undefined = feed?._feedPath;

        const feedEntryExtensionsTag: string | undefined =
          eventData?.feedEntryExtensionsTag;

        if (!feedEntryExtensionsTag)
          throw new Error(`No feed entry extensions tag found`);

        const sessionStorageKey: string = `${feedPath}-${feedEntryExtensionsTag}`;

        const preferenceEditorJsonData: Feed | undefined =
          await requestJsonData({
            url: '/api/preference-editor',
            method: 'POST',
            body: JSON.stringify({
              feedUrl: feedPath,
              feedEntryExtensionsTag,
              role,
              screenFeed,
            }),
            timeout: PREFERENCE_EDITOR_TIMEOUT,
            sessionStorageKey,
          });

        const entry: FeedEntry[] | undefined = preferenceEditorJsonData?.entry;

        if (isEmptyArray(entry))
          throw new Error(`No entry found in preference editor response`);

        setFeedEntries(() => {
          const itemLimit: number | undefined = uiComponent?.rules?.item_limit;
          if (!itemLimit) return entry;
          return entry!.slice(0, getLimit(itemLimit));
        });
      } catch (error: any) {
        isServerLoadedFeedEntry = true;
        logger.info(`Error in preference editor payload: ${error.message}`);
      }
    };

    payloadChannel.addEventListener('message', onMessage);
    return () => {
      clearTimeout(timeoutId);
      if (payloadChannel) {
        payloadChannel.removeEventListener('message', onMessage);
        try {
          payloadChannel.close();
        } catch {}
      }
    };
  }, []);
}

/**
 * Whether an observe_storage component takes part in preference-editor refresh.
 *
 * A producer feed or entry has the `preference_editor` role.
 * A consumer endpoint is tagged `preference_editor_consumer` as well as `observe_storage`.
 * An endpoint tagged only `observe_storage` does not refresh when a preference changes.
 * There is no context-key fallback. A feed that previously matched `storefront.type` refreshes again after its endpoint tags include `preference_editor_consumer`.
 *
 * @param uiComponent - Component enriched with endpoint tags.
 * @param feed - Feed loaded for that component.
 * @returns `true` when the component should keep preference-editor updates.
 */
export function getIsPreferenceEditorByObserveStorageEndpointTag({
  uiComponent,
  feed,
}: {
  uiComponent: UIComponent;
  feed?: Feed;
}): boolean {
  if (!uiComponentRequiresRemoteClientContext(uiComponent)) return false;

  if (feed?.extensions?.role === PREFERENCE_EDITOR_ROLE) return true;

  if (feed?.entry?.some((entry) => entry?._role === PREFERENCE_EDITOR_ROLE)) {
    return true;
  }

  return (
    uiComponent._tags?.some(
      (tag: string) => tag.trim() === PREFERENCE_EDITOR_CONSUMER_TAG
    ) ?? false
  );
}

export function getPreferenceEditorComponentVisibility({
  feed,
  entries,
}: {
  feed: Feed | undefined;
  entries: FeedEntry[];
}): boolean {
  if (feed?.type?.value === 'offers-groups' && isEmptyArray(entries))
    return true;

  return false;
}
